import { ReactElement, use } from 'react';
import { useTranslation } from 'react-i18next';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ModeEditOutlineOutlined from '@mui/icons-material/ModeEditOutlineOutlined';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import WanderingBoundarySettingsProviderContext from 'features/wandering-boundary-settings/providers/contexts/wandering-boundary-provider-context';
import WanderingBoundaryRowProviderContext from 'features/wandering-boundary-settings/providers/contexts/wandering-boundary-row-provider-context';
import styles from './wandering-boundary-row-viewing.module.scss';

const WanderingBoundaryRowViewing = (): ReactElement => {
  const { t } = useTranslation('wanderingBoundarySettings');
  const { wanderingBoundary, isExpanded, setIsExpanded, startEditing } = use(WanderingBoundaryRowProviderContext);
  const {
    detailsState: { removeWanderingBoundary, isEditing },
  } = use(WanderingBoundarySettingsProviderContext);

  const buttonsRender = (): ReactElement => {
    return (
      <>
        <IconButton
          component="div"
          className={styles.cancelButton}
          disabled={isEditing}
          onClick={(e) => {
            e.stopPropagation();
            removeWanderingBoundary(wanderingBoundary);
          }}
        >
          <DeleteForeverOutlinedIcon />
        </IconButton>
        <IconButton
          component="div"
          className={styles.checkButton}
          disabled={isEditing}
          onClick={(e) => {
            e.stopPropagation();
            startEditing();
          }}
        >
          <ModeEditOutlineOutlined className={isEditing ? styles.disabledIcon : ''} />
        </IconButton>
      </>
    );
  };

  return (
    <div className={styles.wanderingBoundaryRowViewingContainer}>
      <Accordion expanded={isExpanded} onChange={(_, expanded) => setIsExpanded(expanded)}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          {isExpanded ? (
            <div className={styles.accordionSummaryContent}>
              <div className={styles.details}>
                <div className={styles.textDisplay}>
                  <Typography variant="subtitle1">{t('wanderingBoundaryFields.nameLabel')}</Typography>
                  <Typography variant="caption">{wanderingBoundary.name}</Typography>
                </div>
                {wanderingBoundary.description !== '' || wanderingBoundary.note !== '' ? (
                  <div className={styles.descriptionAndNoteContainer}>
                    {wanderingBoundary.description !== '' && (
                      <div className={styles.textDisplay}>
                        <Typography variant="subtitle1">{t('wanderingBoundaryFields.descriptionLabel')}</Typography>
                        <Typography variant="caption">{wanderingBoundary.description}</Typography>
                      </div>
                    )}
                    {wanderingBoundary.note !== '' && (
                      <div className={styles.textDisplay}>
                        <Typography variant="subtitle1">{t('wanderingBoundaryFields.noteLabel')}</Typography>
                        <Typography variant="caption">{wanderingBoundary.note}</Typography>
                      </div>
                    )}
                  </div>
                ) : (
                  <></>
                )}
              </div>
              <div className={styles.buttonsContainer}>{buttonsRender()}</div>
            </div>
          ) : (
            <div className={styles.accordionSummaryContentContracted}>
              <Typography className={styles.wanderingBoundaryName} variant="subtitle1">
                {wanderingBoundary.name}
              </Typography>
              <div className={styles.buttonsContainerContracted}>{buttonsRender()}</div>
            </div>
          )}
        </AccordionSummary>
      </Accordion>
    </div>
  );
};

export default WanderingBoundaryRowViewing;
